import React, { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Sparkles,
  Send,
  Plus,
  Loader2,
  Trash2,
  ClipboardCheck,
  MessageSquare,
  Crown,
  Zap,
} from "lucide-react";
import DOMPurify from "dompurify";
import { API_BASE, auth } from "@/lib/api";
import { T } from "@/lib/testIds";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

// Only allow our own markdown-lite tags; strip anything the LLM tries to inject.
const SANITIZE_CFG = { ALLOWED_TAGS: ["strong", "span", "br"], ALLOWED_ATTR: ["class"] };
const sanitize = (html) => DOMPurify.sanitize(html, SANITIZE_CFG);

const SUGGESTIONS = [
  "Who should I captain this week?",
  "Suggest one transfer to improve my team.",
  "Compare Haaland vs Isak for the next 4 GWs.",
  "Which £6m midfielder has the best upcoming fixtures?",
];

function formatDelta(text) {
  return text
    .replace(/</g, "&lt;")
    .replace(/\*\*(.+?)\*\*/g, '<strong class="text-totem-text">$1</strong>')
    .replace(/^- (.+)$/gm, '<span class="block pl-3 relative"><span class="absolute left-0 top-2 w-1 h-1 rounded-full bg-totem-gold"></span>$1</span>')
    .replace(/\n/g, "<br/>");
}

async function apiFetch(path, opts = {}) {
  const token = auth.getToken();
  const headers = { "Content-Type": "application/json", ...(opts.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  const resp = await fetch(`${API_BASE}${path}`, { ...opts, headers });
  if (!resp.ok) {
    let detail = "Request failed";
    let body = null;
    try {
      body = await resp.json();
      detail = body?.detail?.message || body?.detail || detail;
    } catch (_e) {
      // body wasn't JSON (e.g. proxy HTML error page) — surface the generic detail
      if (process.env.NODE_ENV !== "production") console.warn("apiFetch: non-JSON error body", _e);
    }
    const err = new Error(detail);
    err.status = resp.status;
    err.body = body;
    throw err;
  }
  return resp;
}

function QuotaChip({ quota }) {
  if (!quota) return null;
  if (quota.is_pro) {
    return (
      <span
        data-testid="ask-quota-chip"
        className="inline-flex items-center gap-1 mono uppercase text-[10px] tracking-wider px-2 py-1 rounded-full border border-totem-gold/60 text-totem-gold bg-totem-gold/10"
      >
        <Crown size={11} /> Pro · unlimited
      </span>
    );
  }
  const tone = quota.remaining === 0 ? "red" : quota.remaining === 1 ? "gold" : "green";
  return (
    <span
      data-testid="ask-quota-chip"
      className={cn(
        "inline-flex items-center gap-1 mono uppercase text-[10px] tracking-wider px-2 py-1 rounded-full border",
        tone === "red" && "border-totem-red/60 text-totem-red bg-totem-red/10",
        tone === "gold" && "border-totem-gold/60 text-totem-gold bg-totem-gold/10",
        tone === "green" && "border-totem-green/40 text-totem-green bg-totem-green/10",
      )}
    >
      <Zap size={11} /> {quota.remaining}/{quota.limit} left this GW
    </span>
  );
}

function UpgradeCard({ quota, onReset }) {
  return (
    <div
      data-testid="ask-quota-blocked"
      className="mt-3 totem-card border-totem-gold/60 p-5 text-center"
      style={{ boxShadow: "0 0 32px rgba(227,195,43,0.15)" }}
    >
      <div className="grid place-items-center w-14 h-14 rounded-full bg-totem-gold text-totem-bg mx-auto mb-3">
        <Crown size={22} />
      </div>
      <div className="mono uppercase text-[10px] tracking-widest text-totem-gold">Totem Pro</div>
      <h2 className="display-heading text-xl mt-1">You&apos;ve used your 3 free chats this GW</h2>
      <p className="text-sm text-totem-textDim mt-2 leading-relaxed">
        Free plan resets at the end of GW{quota?.current_gw ?? ""}. Upgrade to <strong className="text-totem-gold">Totem Pro</strong> for
        unlimited Ask FPL Scout chats, priority Live Rank Tracker, and Rank Gap benchmarks.
      </p>
      <button
        data-testid="ask-quota-upgrade-cta"
        onClick={() => toast("Totem Pro checkout is coming soon — talk to the admin to enable it.")}
        className="mt-4 w-full bg-totem-gold text-totem-bg font-semibold rounded-lg py-2.5 hover:brightness-110 transition"
      >
        Upgrade to Totem Pro
      </button>
      {onReset && (
        <button
          onClick={onReset}
          className="mt-2 text-[10px] mono uppercase text-totem-textFaint hover:text-totem-textDim tracking-wider"
        >
          Refresh quota
        </button>
      )}
    </div>
  );
}

export default function AskFpl() {
  const qc = useQueryClient();
  const [activeId, setActiveId] = useState(null);
  const [input, setInput] = useState("");
  const [useSquad, setUseSquad] = useState(false);
  const [streamingText, setStreamingText] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const abortRef = useRef(null);

  const { data: quota, refetch: refetchQuota } = useQuery({
    queryKey: ["ask-quota"],
    queryFn: async () => (await apiFetch("/ask/quota")).json(),
    refetchOnWindowFocus: true,
    staleTime: 5_000,
  });
  const blocked = quota && !quota.is_pro && quota.remaining <= 0;

  const { data: sessionsData } = useQuery({
    queryKey: ["ask-sessions"],
    queryFn: async () => (await apiFetch("/ask/sessions")).json(),
    refetchOnWindowFocus: false,
  });
  const sessions = sessionsData?.sessions || [];

  const { data: sessionData } = useQuery({
    queryKey: ["ask-session", activeId],
    queryFn: async () => (await apiFetch(`/ask/sessions/${activeId}`)).json(),
    enabled: !!activeId,
    refetchOnWindowFocus: false,
  });
  const messages = sessionData?.messages || [];

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length, streamingText]);

  const createSession = async (title) => {
    const resp = await apiFetch("/ask/sessions", {
      method: "POST",
      body: JSON.stringify({ title }),
    });
    const s = await resp.json();
    setActiveId(s.id);
    qc.invalidateQueries({ queryKey: ["ask-sessions"] });
    return s.id;
  };

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || sending) return;
    if (blocked) {
      toast.error("Free quota exceeded — upgrade to Pro to keep chatting.");
      return;
    }
    let sid = activeId;
    if (!sid) sid = await createSession(message.slice(0, 60));

    setInput("");
    setStreamingText("");
    setSending(true);
    qc.setQueryData(["ask-session", sid], (old) => {
      if (!old) return old;
      return {
        ...old,
        messages: [
          ...(old.messages || []),
          {
            id: `tmp-${Date.now()}`,
            role: "user",
            content: message,
            used_squad: useSquad,
            created_at: new Date().toISOString(),
          },
        ],
      };
    });

    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const resp = await apiFetch(`/ask/sessions/${sid}/message`, {
        method: "POST",
        body: JSON.stringify({ content: message, use_squad: useSquad }),
        signal: controller.signal,
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let accumulated = "";
      let done = false;
      while (!done) {
        const chunk = await reader.read();
        done = chunk.done;
        if (chunk.value) buffer += decoder.decode(chunk.value, { stream: true });
        let nl;
        while ((nl = buffer.indexOf("\n")) >= 0) {
          const raw = buffer.slice(0, nl).trim();
          buffer = buffer.slice(nl + 1);
          if (!raw) continue;
          try {
            const evt = JSON.parse(raw);
            if (evt.delta) {
              accumulated += evt.delta;
              setStreamingText(accumulated);
            } else if (evt.error) {
              toast.error(evt.error);
            } else if (evt.done) {
              qc.invalidateQueries({ queryKey: ["ask-sessions"] });
              qc.invalidateQueries({ queryKey: ["ask-session", sid] });
              refetchQuota();
            }
          } catch (_e) {
            // NDJSON chunk boundary landed mid-record — safe to skip; will retry on next chunk
            if (process.env.NODE_ENV !== "production") console.warn("ask stream: malformed chunk", raw);
          }
        }
      }
    } catch (e) {
      if (e.name === "AbortError") return;
      if (e.status === 429) {
        toast.error(e.message || "You've used your free chats this week.");
        refetchQuota();
      } else {
        toast.error(e.message || "Something went wrong");
      }
    } finally {
      setSending(false);
      setStreamingText("");
      abortRef.current = null;
    }
  };

  const stopStream = () => abortRef.current?.abort();

  const removeSession = async (id) => {
    if (!window.confirm("Delete this chat?")) return;
    await apiFetch(`/ask/sessions/${id}`, { method: "DELETE" });
    if (id === activeId) setActiveId(null);
    qc.invalidateQueries({ queryKey: ["ask-sessions"] });
  };

  const copyMessage = (txt) => {
    navigator.clipboard?.writeText(txt);
    toast.success("Copied to clipboard");
  };

  const empty = !activeId || messages.length === 0;

  return (
    <div data-testid={T.askPage} className="animate-fade-up flex flex-col h-[calc(100vh-180px)]">
      {/* Header */}
      <div className="flex items-center justify-between mb-2 px-1">
        <div className="flex items-center gap-2">
          <div className="grid place-items-center w-8 h-8 rounded-full bg-totem-gold ai-glow">
            <Sparkles size={16} className="text-totem-bg" strokeWidth={2.5} />
          </div>
          <div>
            <div className="mono uppercase text-[10px] tracking-[0.25em] text-totem-gold">
              Ask FPL Scout
            </div>
            <div className="text-xs text-totem-textDim">Claude Sonnet 4.6 · grounded in live FPL data</div>
          </div>
        </div>
        <button
          data-testid={T.askNewChat}
          onClick={() => { setActiveId(null); setStreamingText(""); }}
          className="flex items-center gap-1 text-xs mono uppercase text-totem-textDim hover:text-totem-gold transition px-2 py-1 rounded border border-totem-borderSoft"
        >
          <Plus size={12} /> New
        </button>
      </div>

      {/* Quota chip */}
      <div className="flex items-center justify-end px-1 mb-2">
        <QuotaChip quota={quota} />
      </div>

      {/* Session pills */}
      {sessions.length > 0 && (
        <div data-testid={T.askSessionList} className="flex gap-1.5 overflow-x-auto pb-2 mb-1 -mx-1 px-1 scrollbar-hide">
          {sessions.map((s) => (
            <button
              key={s.id}
              onClick={() => setActiveId(s.id)}
              data-testid={`ask-session-${s.id}`}
              className={cn(
                "shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border transition",
                activeId === s.id
                  ? "border-totem-gold text-totem-gold bg-totem-gold/10"
                  : "border-totem-borderSoft text-totem-textDim hover:border-totem-border"
              )}
              title={s.title}
            >
              <MessageSquare size={11} />
              <span className="max-w-[140px] truncate">{s.title}</span>
              {activeId === s.id && (
                <span
                  onClick={(e) => { e.stopPropagation(); removeSession(s.id); }}
                  role="button"
                  tabIndex={0}
                  className="cursor-pointer hover:text-totem-red"
                >
                  <Trash2 size={11} />
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Messages */}
      <div data-testid={T.askMessages} className="flex-1 overflow-y-auto totem-card p-3 space-y-3">
        {empty && !blocked && (
          <div className="pt-6 text-center">
            <div className="grid place-items-center w-14 h-14 rounded-full bg-totem-gold/20 mx-auto mb-3">
              <Sparkles size={22} className="text-totem-gold" strokeWidth={2.5} />
            </div>
            <h2 className="display-heading text-lg">Ask the Scout anything</h2>
            <p className="text-xs text-totem-textDim mt-1 max-w-xs mx-auto">
              Grounded in live FPL data. Toggle &quot;Use my squad&quot; for personalised takes.
            </p>
            <div className="mt-4 grid gap-2 max-w-sm mx-auto">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  data-testid={T.askSuggestion}
                  onClick={() => send(s)}
                  className="text-left text-sm px-3 py-2 rounded-lg border border-totem-borderSoft hover:border-totem-gold/60 hover:bg-totem-gold/5 transition"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m) => (
          <div
            key={m.id}
            data-testid={m.role === "assistant" ? T.askAssistantMessage : T.askUserMessage}
            className={cn("flex", m.role === "user" ? "justify-end" : "justify-start")}
          >
            <div
              className={cn(
                "max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed",
                m.role === "user"
                  ? "bg-totem-green text-totem-bg rounded-br-md"
                  : "bg-totem-surfaceElev text-totem-text border border-totem-borderSoft rounded-bl-md"
              )}
            >
              {m.role === "assistant" && (
                <div className="flex items-center justify-between mb-1">
                  <span className="mono text-[9px] uppercase tracking-wider text-totem-gold">Scout</span>
                  <button
                    onClick={() => copyMessage(m.content)}
                    className="text-totem-textFaint hover:text-totem-gold transition"
                    title="Copy"
                  >
                    <ClipboardCheck size={11} />
                  </button>
                </div>
              )}
              <div
                className={m.role === "user" ? "whitespace-pre-wrap" : ""}
                dangerouslySetInnerHTML={{
                  __html: sanitize(
                    m.role === "assistant" ? formatDelta(m.content) : m.content.replace(/</g, "&lt;")
                  ),
                }}
              />
              {m.used_squad && m.role === "user" && (
                <div className="mono text-[9px] uppercase mt-1 opacity-70">· with squad context</div>
              )}
            </div>
          </div>
        ))}

        {streamingText && (
          <div className="flex justify-start">
            <div className="max-w-[85%] rounded-2xl rounded-bl-md px-3.5 py-2.5 text-sm leading-relaxed bg-totem-surfaceElev text-totem-text border border-totem-gold/40">
              <div className="mono text-[9px] uppercase tracking-wider text-totem-gold mb-1 flex items-center gap-1">
                <Loader2 size={10} className="animate-spin" /> Scout is thinking
              </div>
              <div dangerouslySetInnerHTML={{ __html: sanitize(formatDelta(streamingText)) }} />
            </div>
          </div>
        )}

        {sending && !streamingText && (
          <div className="flex items-center gap-2 text-totem-textDim text-xs animate-pulse">
            <Loader2 size={12} className="animate-spin" /> Consulting FPL data…
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Input or Upgrade CTA */}
      {blocked ? (
        <UpgradeCard quota={quota} onReset={refetchQuota} />
      ) : (
        <div className="mt-2">
          <label className="flex items-center gap-2 text-xs text-totem-textDim mb-2 px-1 cursor-pointer">
            <input
              type="checkbox"
              data-testid={T.askSquadToggle}
              checked={useSquad}
              onChange={(e) => setUseSquad(e.target.checked)}
              className="w-3.5 h-3.5 accent-[#E3C32B]"
            />
            <span className="mono uppercase tracking-wider">Use my squad context</span>
          </label>
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="flex items-end gap-2 bg-totem-surfaceElev border border-totem-borderSoft rounded-2xl p-2 focus-within:border-totem-gold/60 transition"
          >
            <textarea
              data-testid={T.askInput}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
              }}
              placeholder="Ask about captains, transfers, fixtures…"
              rows={1}
              className="flex-1 bg-transparent resize-none max-h-32 focus:outline-none text-sm placeholder:text-totem-textFaint px-1 py-1"
            />
            {sending ? (
              <button
                type="button"
                onClick={stopStream}
                className="shrink-0 rounded-full bg-totem-red text-white h-9 w-9 grid place-items-center hover:brightness-110 transition"
                title="Stop"
              >
                <div className="w-2.5 h-2.5 bg-white rounded-sm" />
              </button>
            ) : (
              <button
                type="submit"
                data-testid={T.askSend}
                disabled={!input.trim()}
                className="shrink-0 rounded-full bg-totem-gold text-totem-bg h-9 w-9 grid place-items-center hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                <Send size={16} strokeWidth={2.4} />
              </button>
            )}
          </form>
          <p className="text-[10px] mono text-totem-textFaint mt-2 px-1">
            The Scout can be wrong — double-check stats in Players tab.
          </p>
        </div>
      )}
    </div>
  );
}
